import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  ExecutionResponse,
  FunctionExecuteRequest,
  FunctionMetadata,
  WasabiFunctionsService,
} from '../../../../core/services/wasabi/wasabi-functions.service';
import { Observable } from 'rxjs';
import { ResultTabs, WasabiForm } from './wasabi-utils';
import { map } from 'rxjs/operators';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { swalMini } from '../../../../shared/utils/utils';

@Component({
  selector: 'app-wasabi-functions',
  templateUrl: './wasabi-functions.component.html',
  styleUrls: ['./wasabi-functions.component.scss'],
})
export class WasabiFunctionsComponent implements OnInit, OnDestroy {

  readonly headers: string[] = ['Function id', 'Name', 'Description', 'Script Path', 'Mc2 Account Id', 'Active', 'Parameters'];

  public allFunctionsMetadata$: Observable<FormArray> = undefined;
  public functionMetaDataForm: FormArray = new FormArray([]);
  public canEdit = true;
  public testFormGroup: FormGroup = undefined;
  public selectedIndex: ResultTabs = ResultTabs.Results;
  public selectedResultIndex: number;
  public executionResponse: ExecutionResponse;
  public saveClicked = false;

  @ViewChild('resultEditor') resultEditor: ElementRef;
  public monacoResults: monaco.editor.IStandaloneCodeEditor;

  @ViewChild('resultInfoEditor') resultInfoEditor: ElementRef;
  public monacoInfoResults: monaco.editor.IStandaloneCodeEditor;

  @ViewChild('resultErrorEditor') resultErrorEditor: ElementRef;
  public monacoErrorResults: monaco.editor.IStandaloneCodeEditor;

  constructor(private wasabiFunctionsService: WasabiFunctionsService) {
  }

  ngOnInit(): void {
    this.allFunctionsMetadata$ = this.wasabiFunctionsService.getAllFunctions()
      .pipe(
        map((functionMetadata: FunctionMetadata[]) => this.buildFormData(functionMetadata)),
      );
  }

  ngOnDestroy(): void {
    this.monacoDispose();
  }

  public tabChange(event: MatTabChangeEvent): void {
    this.selectedIndex = event.index;
    if (event.index === 1 && this.testFormGroup === undefined) {
      const data: FunctionMetadata = {
        id: '',
        scriptPath: '',
        active: true,
        description: '',
        name: '',
        params: [],
        mc2AccountId: '',
      };
      this.testFormGroup = WasabiForm.buildTestForm(data, '', true);
      this.selectedResultIndex = ResultTabs.Results;
    } else if (event.index === 0) {
      this.testFormGroup = undefined;
      this.executionResponse = undefined;
      this.monacoDispose();
    }
  }

  public resultTabChange(event: MatTabChangeEvent): void {
    this.selectedResultIndex = event.index;
    this.monacoDispose();
    if (this.executionResponse) {
      switch (event.index) {
        case ResultTabs.Results:
          if (!this.monacoResults) {
            const value = JSON.stringify(this.executionResponse.result, null, 4);
            this.monacoResults = this.setupEditor(this.resultEditor.nativeElement, true, value);
          }
          break;
        case ResultTabs.Info:
          if (!this.monacoInfoResults) {
            this.monacoInfoResults = this.setupEditor(this.resultInfoEditor.nativeElement, true, this.executionResponse.info);
          }
          break;
        case ResultTabs.Error:
          if (!this.monacoErrorResults) {
            this.monacoErrorResults = this.setupEditor(this.resultErrorEditor.nativeElement, true, this.executionResponse.error);
          }
          break;
      }
    }
  }

  private monacoDispose() {
    if (this.monacoResults) {
      this.monacoResults.dispose();
      this.monacoResults = undefined;
    }
    if (this.monacoInfoResults) {
      this.monacoInfoResults.dispose();
      this.monacoInfoResults = undefined;
    }
    if (this.monacoErrorResults) {
      this.monacoErrorResults.dispose();
      this.monacoErrorResults = undefined;
    }
  }

  private buildFormData(functionMetadata: FunctionMetadata[]): FormArray {
    for (const metadata of functionMetadata) {
      this.functionMetaDataForm.push(WasabiForm.formTemplate(metadata, this.canEdit));
    }
    return this.functionMetaDataForm;
  }

  public openParams(element: HTMLDivElement): void {
    if (element.classList.contains('close')) {
      element.className = 'params-container open';
    } else {
      element.className = 'params-container close';
    }
  }

  public saveOrUpdate(rowIndex: number): void {
    this.saveClicked = true;

    if (this.checkForDuplicateScripts(rowIndex)) {
      swalMini({
        title: 'The script path you have entered is in use.',
        type: 'error',
      });
      return;
    }

    const functionForm = this.functionMetaDataForm.get(`${rowIndex}`);
    const paramsArray = functionForm.get('params');

    // Check if any parameter control has validation errors
    if (paramsArray && paramsArray instanceof FormArray) {
      for (const paramControl of paramsArray.controls) {
        if (paramControl instanceof FormGroup) {
          const nameControl = paramControl.get('name');
          const descriptionControl = paramControl.get('description');

          // Only check for errors if either field has been interacted with
          if ((nameControl?.touched || nameControl?.dirty ||
              descriptionControl?.touched || descriptionControl?.dirty) &&
            ((nameControl && nameControl.errors) ||
              (descriptionControl && descriptionControl.errors))) {
            swalMini({
              title: 'Parameters validation failed',
              text: 'Please fix the parameter validation errors before saving.',
              type: 'error',
            });
            return;
          }
        }
      }
    }

    const rawValue = this.functionMetaDataForm.getRawValue()[rowIndex];

    if (rawValue['id'] && rawValue['id'] !== '') {
      this.wasabiFunctionsService.updateFunction(WasabiForm.updateFunctionRequest(rawValue)).subscribe(
        () => {
          swalMini({
            title: 'Successfully updated function.',
            type: 'success',
          });
        },
        (error) => {
          let errorMessage = '';
          if (error?.error?.message) {
            try {
              const parsedMessage = JSON.parse(error.error.message);
              errorMessage = parsedMessage.message ? ` ${parsedMessage.message}` : '';
            } catch {
              errorMessage = error.error.message ? ` ${error.error.message}` : '';
            }
          }

          swalMini({
            title: `Failed to update function${errorMessage}`,
            type: 'error',
          });
        },
      );
    } else {
      this.wasabiFunctionsService.saveFunction(WasabiForm.createFunctionRequest(rawValue)).subscribe(
        (response) => {
          const abstractControl: FormGroup = this.functionMetaDataForm.get(`${rowIndex}`) as FormGroup;
          abstractControl.get('id').setValue(response.id);
          WasabiForm.setCanEditParams(abstractControl);
          swalMini({
            title: 'Successfully saved function.',
            type: 'success',
          });
        },
        (error) => {
          let errorMessage = '';
          if (error?.error?.message) {
            try {
              const parsedMessage = JSON.parse(error.error.message);
              errorMessage = parsedMessage.message ? ` ${parsedMessage.message}` : '';
            } catch {
              errorMessage = error.error.message ? ` ${error.error.message}` : '';
            }
          }

          swalMini({
            title: `Failed to save function${errorMessage}`,
            type: 'error',
          });
        },
      );
    }
  }

  private checkForDuplicateScripts(currentRowIndex: number): boolean {
    const currentScript = this.functionMetaDataForm.getRawValue()[currentRowIndex].scriptPath;
    const trimmedCurrentScript = currentScript?.trim();

    // Early return if current script is undefined or empty
    if (trimmedCurrentScript === '') {
      return false;
    }

    return this.functionMetaDataForm.controls
      .some((control: AbstractControl, index: number) => {
        if (index === currentRowIndex) {
          return false;
        }

        const otherScript = control.get('scriptPath').value;

        // Skip comparison if other script is undefined or empty
        if (!otherScript || otherScript.trim() === '') {
          return false;
        }

        return trimmedCurrentScript === otherScript.trim();
      });
  }

  public addFunction(): void {
    const formGroup: FormGroup = WasabiForm.formDefaultTemplate(this.canEdit);
    const paramsControl: FormArray = formGroup.get('params') as FormArray;

    paramsControl.push(WasabiForm.paramFormTemplate(this.canEdit));
    this.functionMetaDataForm.push(formGroup);

    const index = this.functionMetaDataForm.length - 1;
    setTimeout(() => {
      document.getElementById(`func-${index}`).scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  public deleteFunction(functionId: string, rowIndex: number): void {
    if (!functionId || !functionId.length) {
      this.deleteFunctionMetaData(rowIndex);
    } else {
      this.wasabiFunctionsService.deleteFunction(functionId).subscribe(
        (metaData: FunctionMetadata): void => {
          if (metaData.id === functionId) {
            this.deleteFunctionMetaData(rowIndex);
          }
        },
      );
    }
  }

  public deleteParam(rowIndex: number, paramIndex: number): void {
    const abstractControl: FormArray = this.functionMetaDataForm.get(`${rowIndex}.params`) as FormArray;
    if (abstractControl.length > 1) {
      abstractControl.removeAt(paramIndex);
    }
  }

  public addParam(rowIndex: number): void {
    const abstractControl: FormArray = this.functionMetaDataForm.get(`${rowIndex}.params`) as FormArray;
    abstractControl.push(WasabiForm.paramFormTemplate(this.canEdit));
  }

  private deleteFunctionMetaData(rowIndex: number): void {
    this.functionMetaDataForm.removeAt(rowIndex);
  }

  public toTest(formGroup: AbstractControl) {
    const form = formGroup as FormGroup;
    this.testFormGroup = WasabiForm.buildTestForm(form.getRawValue(), 'master', false);
    this.selectedIndex = 1;
    this.selectedResultIndex = ResultTabs.Info;
  }

  public executeTest(): void {
    this.testFormGroup.markAllAsTouched();
    if (this.testFormGroup.valid) {
      this.monacoDispose();
      const body: FunctionExecuteRequest = WasabiForm.executePayload(this.testFormGroup.getRawValue());
      this.wasabiFunctionsService.execute(body).subscribe(response => {
        this.executionResponse = response;

        const value = JSON.stringify(this.executionResponse.result, null, 4);
        this.monacoResults = this.setupEditor(this.resultEditor.nativeElement, true, value);
        if (this.executionResponse.error && this.executionResponse.error !== '') {
          this.selectedResultIndex = ResultTabs.Error;
        } else {
          this.selectedResultIndex = ResultTabs.Results;
        }
      });
    }
  }

  public addTestParam(): void {
    const abstractControl: FormArray = this.testFormGroup.get('params') as FormArray;
    abstractControl.push(WasabiForm.paramTestFormTemplate('', true));
  }

  private setupEditor(elementRef, readOnly: boolean, value: string) {
    return monaco.editor.create(elementRef, {
      value,
      minimap: { enabled: false },
      fontSize: 10,
      theme: 'vs-light',
      readOnly: readOnly,
    });
  }

  public deleteTestParam(paramIndex) {
    const paramsArray: FormArray = this.testFormGroup.get('params') as FormArray;

    if (paramIndex >= 0 && paramIndex < paramsArray.length) {
      paramsArray.removeAt(paramIndex);
    }
  }
}
